// Prüfen und Übernehmen der Altarchiv-Vorschläge (#517, Etappe C/D).
// Liest altarchiv_uebernahme mit ausdrücklichen Spalten (Verbrauchsspalten sind fürs Team gesperrt),
// legt über die Edge Function 'altarchiv-uebernahme' ab und setzt Ablehnen/Zurück direkt (RLS: Staff).
import { supabase } from '../supabase/client';

export { ALTARCHIV_ORDNER, innererPfad, jahrAusText, ohneDatumImNamen, mitAltarchiv, pruefeAblage, setzeDatumImNamen, stichprobeBestanden, STICHPROBE_MINDEST, STICHPROBE_MITTEL_MINDEST, type MassenStufe } from '../../supabase/functions/altarchiv-uebernahme/pruefung';
import type { MassenStufe } from '../../supabase/functions/altarchiv-uebernahme/pruefung';

export type PruefSegment = 'vorschlag' | 'ohne_objekt' | 'abgelegt' | 'abgelehnt';
export type SicherheitsFilter = 'alle' | 'hoch' | 'mittel' | 'niedrig';

interface Person { vorname: string | null; nachname: string | null; firma_name: string | null }

export interface PruefZeile {
  dokument_id: string;
  status: string;
  objekt_id: string | null;
  alter_pfad: string | null;
  neuer_name: string | null;
  ziel_pfad: string | null;
  ziel_quelle: string | null;
  dokumentart: string | null;
  dokument_datum: string | null;
  datum_quelle: string | null;
  absender: string | null;
  sicherheit: number | null;
  begruendung: string | null;
  text_quelle: string | null;
  fehler: string | null;
  korrigiert: boolean;
  gdrive_file_id: string | null;
  bearbeitet_am: string | null;
  ablehnungsgrund: string | null;
  objekt: { objektnummer: string | null; bezeichnung: string | null } | null;
  dokument: { datei_name: string | null; datei_groesse: number | null; onedrive_url: string | null } | null;
  mieter: Person | null;
  eigentuemer: Person | null;
  einheit: { bezeichnung: string | null } | null;
  dienstleister: { name: string | null } | null;
}

const SPALTEN = [
  'dokument_id, status, objekt_id, alter_pfad, neuer_name, ziel_pfad, ziel_quelle, dokumentart, dokument_datum, datum_quelle',
  'absender, sicherheit, begruendung, text_quelle, fehler, korrigiert, gdrive_file_id, bearbeitet_am, ablehnungsgrund',
  'objekt:objekt_id(objektnummer, bezeichnung)',
  'dokument:dokumente!altarchiv_uebernahme_dokument_id_fkey(datei_name, datei_groesse, onedrive_url)',
  'mieter:mieter_id(vorname, nachname, firma_name)',
  'eigentuemer:eigentuemer_id(vorname, nachname, firma_name)',
  'einheit:einheit_id(bezeichnung)',
  'dienstleister:dienstleister_id(name)',
].join(', ');

export const PRUEF_SEITE = 20;

export interface PruefFilter { segment: PruefSegment; objektId: string | null; sicherheit: SicherheitsFilter }

/** Grenzen der Sicherheitsstufen wie in der Fortschrittskarte. */
export function sicherheitsGrenzen(f: SicherheitsFilter): { ab?: number; unter?: number } {
  if (f === 'hoch') return { ab: 0.9 };
  if (f === 'mittel') return { ab: 0.7, unter: 0.9 };
  if (f === 'niedrig') return { unter: 0.7 };
  return {};
}

function abfrage(f: PruefFilter, zaehlen: boolean) {
  let q = supabase.from('altarchiv_uebernahme').select(SPALTEN, zaehlen ? { count: 'exact' } : undefined).eq('status', f.segment);
  if (f.objektId) q = q.eq('objekt_id', f.objektId);
  if (f.segment === 'vorschlag') {
    const g = sicherheitsGrenzen(f.sicherheit);
    if (g.ab !== undefined) q = q.gte('sicherheit', g.ab);
    if (g.unter !== undefined) q = q.lt('sicherheit', g.unter);
  }
  return q;
}

export async function ladeSeite(f: PruefFilter, von: number): Promise<{ zeilen: PruefZeile[]; anzahl: number | null }> {
  let q = abfrage(f, von === 0);
  q = f.segment === 'abgelegt' || f.segment === 'abgelehnt'
    ? q.order('bearbeitet_am', { ascending: false, nullsFirst: false })
    : q.order('sicherheit', { ascending: false, nullsFirst: false });
  const { data, error, count } = await q.order('dokument_id').range(von, von + PRUEF_SEITE - 1);
  if (error) throw error;
  return { zeilen: (data ?? []) as unknown as PruefZeile[], anzahl: count ?? null };
}

export async function zaehleSegmente(objektId: string | null): Promise<Record<PruefSegment, number>> {
  const segmente: PruefSegment[] = ['vorschlag', 'ohne_objekt', 'abgelegt', 'abgelehnt'];
  const werte = await Promise.all(segmente.map(async s => {
    let q = supabase.from('altarchiv_uebernahme').select('dokument_id', { count: 'exact', head: true }).eq('status', s);
    if (objektId) q = q.eq('objekt_id', objektId);
    const { count, error } = await q;
    if (error) throw error;
    return count ?? 0;
  }));
  return Object.fromEntries(segmente.map((s, i) => [s, werte[i]])) as Record<PruefSegment, number>;
}

async function fehlerAus(error: unknown, sonst: string): Promise<Error> {
  let msg = (error as Error)?.message || sonst;
  const resp: any = (error as any)?.context;
  if (resp && typeof resp.json === 'function') {
    try { const b = await resp.json(); if (b?.error) msg = b.error; } catch { /* ignore */ }
  }
  return new Error(msg);
}

export interface AblageWerte { objektId: string; zielPfad: string; dateiname: string }

export async function legeAltarchivAb(dokumentId: string, w: AblageWerte, massenablage = false): Promise<{ gdriveFileId: string; schonAbgelegt?: boolean; statusFehler?: string | null }> {
  const { data, error } = await supabase.functions.invoke('altarchiv-uebernahme', {
    body: { aktion: 'ablegen', dokumentId, objektId: w.objektId, zielPfad: w.zielPfad, dateiname: w.dateiname.trim(), massenablage },
  });
  if (error) throw await fehlerAus(error, 'Übernehmen fehlgeschlagen');
  return data;
}

export async function setzeStatus(dokumentId: string, status: 'abgelehnt' | 'vorschlag' | 'ohne_objekt', userId: string | null): Promise<void> {
  const { error } = await supabase.from('altarchiv_uebernahme')
    // Zurück in die Prüfung: ein gesetzter Ablehnungsgrund (z. B. „Privat“) gilt nicht mehr.
    .update({ status, bearbeitet_von: userId, bearbeitet_am: new Date().toISOString(), updated_at: new Date().toISOString(), ...(status === 'abgelehnt' ? {} : { ablehnungsgrund: null }) })
    .eq('dokument_id', dokumentId);
  if (error) throw error;
}

/** Zurück in die Prüfung: mit Objekt als Vorschlag, sonst unter „ohne Objekt“. */
export const zurueckStatus = (z: Pick<PruefZeile, 'objekt_id'>): 'vorschlag' | 'ohne_objekt' => (z.objekt_id ? 'vorschlag' : 'ohne_objekt');

/** PDF aus OneDrive als Blob; der Typ ist hart auf application/pdf gesetzt (keine HTML/SVG-Ausführung). */
export async function ladeVorschau(dokumentId: string): Promise<Blob> {
  const { data, error } = await supabase.functions.invoke('altarchiv-uebernahme', { body: { aktion: 'vorschau', dokumentId } });
  if (error) throw await fehlerAus(error, 'Vorschau fehlgeschlagen');
  const roh = data instanceof Blob ? data : new Blob([data as BlobPart]);
  return new Blob([await roh.arrayBuffer()], { type: 'application/pdf' });
}

export interface Stichprobe { hoch: { einzeln: number; korrigiert: number }; mittel: { einzeln: number; korrigiert: number } }

export async function ladeStichprobe(): Promise<Stichprobe> {
  const { data, error } = await supabase.rpc('altarchiv_stichprobe');
  if (error) throw error;
  const d = (data ?? {}) as Record<string, number>;
  return {
    hoch: { einzeln: d.einzeln ?? 0, korrigiert: d.korrigiert ?? 0 },
    mittel: { einzeln: d.mittel_einzeln ?? 0, korrigiert: d.mittel_korrigiert ?? 0 },
  };
}

/** Kandidaten der Massenablage eines Objekts je Stufe: mit Ordner und vollständigem Datum. */
export async function ladeMassenKandidaten(objektId: string, stufe: MassenStufe = 'hoch'): Promise<PruefZeile[]> {
  const alle: PruefZeile[] = [];
  for (let von = 0; ; von += 500) {
    let q = supabase.from('altarchiv_uebernahme').select(SPALTEN).eq('status', 'vorschlag').eq('objekt_id', objektId);
    q = stufe === 'hoch' ? q.gte('sicherheit', 0.9) : q.gte('sicherheit', 0.7).lt('sicherheit', 0.9);
    const { data, error } = await q
      .not('ziel_pfad', 'is', null).not('neuer_name', 'like', 'XX\\_XX\\_XXXX\\_%')
      .order('dokument_id').range(von, von + 499);
    if (error) throw error;
    alle.push(...((data ?? []) as unknown as PruefZeile[]));
    if ((data ?? []).length < 500) return alle;
  }
}

export function personName(p: Person | null): string | null {
  if (!p) return null;
  return [p.firma_name, [p.vorname, p.nachname].filter(Boolean).join(' ')].filter(Boolean).join(' / ') || null;
}

/** Bezüge als kurze Liste für die Karte. */
export function bezuege(z: PruefZeile): { art: string; text: string }[] {
  return [
    z.einheit?.bezeichnung ? { art: 'Einheit', text: z.einheit.bezeichnung } : null,
    personName(z.mieter) ? { art: 'Mieter', text: personName(z.mieter) as string } : null,
    personName(z.eigentuemer) ? { art: 'Eigentümer', text: personName(z.eigentuemer) as string } : null,
    z.dienstleister?.name ? { art: 'Dienstleister', text: z.dienstleister.name } : null,
  ].filter((x): x is { art: string; text: string } => !!x);
}
